/**
 * Custom logger that enhances console methods with additional features
 * - Includes timestamp
 * - Only logs in development by default
 * - Can be silenced by environment variable
 * - Supports additional context data
 */
const logger = {
  /**
   * Log an informational message
   * @param {string} message - The message to log
   * @param {Object} data - Optional data to include
   */
  info: (message, data) => {
    logWithLevel('info', message, data);
  },

  /**
   * Log a debug message (only in development)
   * @param {string} message - The message to log
   * @param {Object} data - Optional data to include
   */
  debug: (message, data) => {
    logWithLevel('debug', message, data);
  },

  /**
   * Log a warning message
   * @param {string} message - The message to log
   * @param {Object} data - Optional data to include
   */
  warn: (message, data) => {
    logWithLevel('warn', message, data);
  },

  /**
   * Log an error message
   * @param {string} message - The message to log
   * @param {Object|Error} data - Optional error object or context data
   */
  error: (message, data) => {
    logWithLevel('error', message, data);
  }
};

/**
 * Internal helper function for logging with levels
 * @param {string} level - Log level (info, debug, warn, error)
 * @param {string} message - Message to log
 * @param {any} data - Additional data
 */
function logWithLevel(level, message, data) {
  // Skip logs in production unless explicitly enabled
  if (process.env.NODE_ENV === 'production' && level === 'debug' && !process.env.REACT_APP_ENABLE_DEBUG_LOGS) {
    return;
  }

  // Skip all logs if explicitly disabled
  if (process.env.REACT_APP_DISABLE_LOGS === 'true') {
    return;
  }

  const timestamp = new Date().toISOString();
  const prefix = `[${timestamp}] [${level.toUpperCase()}]`;

  // Prepare the data object
  let logData = data;
  if (data instanceof Error) {
    logData = {
      name: data.name,
      message: data.message,
      stack: data.stack
    };
  }

  if (logData) {
    console[level](prefix, message, logData);
  } else {
    console[level](prefix, message);
  }
}

export default logger; 